import { authFetch } from '../../api/csrf';
import React, { useState, useEffect } from 'react';
import {
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Eye,
  Download,
  PlayCircle,
  PauseCircle,
  RefreshCw,
} from 'lucide-react';
import { policyService, acknowledgmentService } from '../../api/services/complianceService';
import { useAuth } from '../../context/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDate } from '../../utils/stringUtils';
import PaginationControls from '../../components/common/Pagination/PaginationControls';

const formatLabel = (value) => {
  if (!value && value !== 0) return value;
  return String(value)
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''))
    .join(' ')
    .trim();
};

const PoliciesCompliance = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  // Pagination state
  const [policiesCurrentPage, setPoliciesCurrentPage] = useState(1);
  const policiesItemsPerPage = 10;
  const [policiesTotalPages, setPoliciesTotalPages] = useState(1);
  const [policiesTotal, setPoliciesTotal] = useState(0);

  // Fetch KPI stats (all-pages totals, not just current page)
  const { data: statsResponse, refetch: refetchStats } = useQuery({
    queryKey: ['policies', 'my-stats'],
    queryFn: () => policyService.getMyPolicyStats(),
    staleTime: 0,
    retry: 2,
  });

  const kpiStats = React.useMemo(() => {
    if (!statsResponse) return { total: 0, acknowledged: 0, pending: 0 };
    const d = statsResponse.data || statsResponse;
    return {
      total: d.total ?? 0,
      acknowledged: d.acknowledged ?? 0,
      pending: d.pending ?? 0,
    };
  }, [statsResponse]);

  // Fetch policies from API using React Query
  const {
    data: policiesResponse,
    isLoading: policiesLoading,
    error: policiesError
  } = useQuery({
    queryKey: ['policies', 'active', policiesCurrentPage],
    queryFn: async () => {
      const response = await policyService.getPolicies({
        status: 'active',
        page: policiesCurrentPage,
        limit: policiesItemsPerPage
      });
      return response;
    },
    staleTime: 0,
    retry: 3,
  });

  // Fetch user acknowledgments using React Query
  const {
    data: acknowledgmentsResponse,
    isLoading: acknowledgmentsLoading,
    error: acknowledgmentsError,
    refetch: refetchAcknowledgments
  } = useQuery({
    queryKey: ['acknowledgments', user?.id || user?._id],
    queryFn: async () => {
      if (!user?.id && !user?._id) return { items: [] };
      const response = await acknowledgmentService.getAcknowledgments();
      return response;
    },
    enabled: !!user,
    staleTime: 0,
    retry: 2,
  });

  // Handle different response structures with pagination
  const policiesList = React.useMemo(() => {
    if (!policiesResponse) {
      setPoliciesTotal(0);
      setPoliciesTotalPages(1);
      return [];
    }

    let policies = [];
    let total = 0;
    let totalPages = 1;

    // Handle different response structures
    if (policiesResponse.data) {
      // Check for { data: { policies: [...], pagination: {...} } }
      if (policiesResponse.data.policies && Array.isArray(policiesResponse.data.policies)) {
        policies = policiesResponse.data.policies;
        total = policiesResponse.data.pagination?.total || policies.length;
        totalPages = policiesResponse.data.pagination?.totalPages || Math.ceil(total / policiesItemsPerPage) || 1;
      }
      // Check for { data: { data: [...], pagination: {...} } }
      else if (policiesResponse.data.data && Array.isArray(policiesResponse.data.data)) {
        policies = policiesResponse.data.data;
        total = policiesResponse.data.pagination?.total || policies.length;
        totalPages = policiesResponse.data.pagination?.totalPages || Math.ceil(total / policiesItemsPerPage) || 1;
      }
      // Check for { data: [...], pagination: {...} }
      else if (Array.isArray(policiesResponse.data)) {
        policies = policiesResponse.data;
        total = policiesResponse.pagination?.total || policies.length;
        totalPages = policiesResponse.pagination?.totalPages || Math.ceil(total / policiesItemsPerPage) || 1;
      }
    }
    // Check for direct array structure
    else if (Array.isArray(policiesResponse)) {
      policies = policiesResponse;
      total = policies.length;
      totalPages = Math.ceil(total / policiesItemsPerPage) || 1;
    }
    // Check for { policies: [...], pagination: {...} }
    else if (policiesResponse.policies && Array.isArray(policiesResponse.policies)) {
      policies = policiesResponse.policies;
      total = policiesResponse.pagination?.total || policies.length;
      totalPages = policiesResponse.pagination?.totalPages || Math.ceil(total / policiesItemsPerPage) || 1;
    }

    // Update pagination state
    setPoliciesTotal(total);
    setPoliciesTotalPages(totalPages);

    return policies;
  }, [policiesResponse, policiesItemsPerPage, policiesCurrentPage]);

  let acknowledgments = [];
  if (acknowledgmentsResponse) {
    // Handle paginated response structure: { items: [...], pagination: {...} }
    if (acknowledgmentsResponse.items && Array.isArray(acknowledgmentsResponse.items)) {
      acknowledgments = acknowledgmentsResponse.items;
    } else if (acknowledgmentsResponse.data && Array.isArray(acknowledgmentsResponse.data)) {
      acknowledgments = acknowledgmentsResponse.data;
    } else if (Array.isArray(acknowledgmentsResponse)) {
      acknowledgments = acknowledgmentsResponse;
    }
  }

  // Map policies with acknowledgment status
  const policiesWithAcknowledgment = policiesList.map(policy => {
    const policyId = (policy.id || policy._id)?.toString();
    const acknowledgment = acknowledgments.find(ack => {
      const ackPolicyId = (ack.policyId || (ack.policy && (ack.policy.id || ack.policy._id || ack.policy)))?.toString();
      // Compare as strings to handle ObjectId vs string matching
      return ackPolicyId === policyId || ackPolicyId === (policy.id || policy._id)?.toString();
    });
    const isAcknowledged = !!acknowledgment && acknowledgment.status === 'acknowledged';
    return {
      ...policy,
      isAcknowledged: isAcknowledged,
      acknowledgedDate: acknowledgment?.acknowledgedDate || acknowledgment?.createdAt || null,
    };
  });

  const loading = policiesLoading || acknowledgmentsLoading;

  // Acknowledge policy mutation using React Query
  const acknowledgeMutation = useMutation({
    mutationFn: async (policyId) => {
      return await acknowledgmentService.createAcknowledgment({
        policyId: policyId,
        action: 'acknowledge'
      });
    },
    onSuccess: async () => {
      // Invalidate and refetch acknowledgments and stats to update UI immediately
      queryClient.invalidateQueries({
        queryKey: ['acknowledgments', user?.id || user?._id],
        exact: false
      });
      queryClient.invalidateQueries({ queryKey: ['policies', 'my-stats'] });

      // Explicitly refetch to ensure UI updates
      try {
        await Promise.all([refetchAcknowledgments(), refetchStats()]);
      } catch (error) {
      }

      alert('Policy acknowledged successfully!');
    },
    onError: (error) => {
      alert('Failed to acknowledge policy. Please try again.');
    },
  });


  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-blue-600 bg-blue-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      case 'active': return 'text-blue-600 bg-blue-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'overdue': return <AlertCircle className="w-4 h-4" />;
      case 'active': return <PlayCircle className="w-4 h-4" />;
      case 'inactive': return <PauseCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getPolicyDownloadFilename = (policy, legacyData = null) => {
    const pdfData = legacyData || policy?.pdfFilePath || policy?.pdfUrl;
    if (!pdfData) return "policy.pdf";

    // If pdfData is the metadata object
    if (typeof pdfData === 'object' && pdfData !== null) {
      const fallbackName = pdfData.originalName || pdfData.name || policy?.title || "policy";
      if (/\.[a-zA-Z0-9]+$/.test(fallbackName)) return fallbackName;

      const mimeType = (pdfData.mimetype || pdfData.mimeType || "").toLowerCase();
      if (mimeType === "application/pdf") return `${fallbackName}.pdf`;
      return `${fallbackName}.pdf`;
    }

    // Fallback for string URLs
    const pdfUrl = String(pdfData || '');
    const parts = pdfUrl.split('/');
    const filenameFromUrl = parts[parts.length - 1] || "policy.pdf";
    const baseName = policy?.title || filenameFromUrl;

    if (/\.[a-zA-Z0-9]+$/.test(baseName)) return baseName;
    return `${baseName.toLowerCase().endsWith('.pdf') ? baseName : baseName + '.pdf'}`;
  };

  const handleAcknowledgePolicy = (policyId) => {
    acknowledgeMutation.mutate(policyId);
  };

  const handleDownloadPolicy = async (policy) => {
    if (!policy) return;

    try {
      // Determine the PDF URL, handling cases where it might be an object or a string
      let pdfUrl = policy.pdfUrl || policy.pdfFilePath;

      // If pdfUrl is an object, try to extract the URL property
      if (pdfUrl && typeof pdfUrl === 'object') {
        pdfUrl = pdfUrl.url || pdfUrl.fileUrl || pdfUrl.path || pdfUrl.secure_url;
      }

      if (pdfUrl && typeof pdfUrl === 'string') {
        // Get proper filename with extension
        const fileName = getPolicyDownloadFilename(policy);

        try {
          // Fetch the PDF as a blob to handle cross-origin downloads
          const response = await authFetch(pdfUrl);
          if (!response.ok) throw new Error('Failed to fetch PDF');
          
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          
          // Create download link from blob URL (same-origin, so download attribute works)
          const link = document.createElement('a');
          link.href = blobUrl;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          
          // Clean up blob URL
          window.URL.revokeObjectURL(blobUrl);
        } catch (fetchError) {
          // Fallback: open in new tab
          window.open(pdfUrl, '_blank');
        }
      } else {
        alert('PDF not available for this policy');
      }
    } catch (error) {
      alert('Failed to download policy. Please try again.');
    }
  };



  const renderPolicies = () => (
    <div className="space-y-4">
      {/* Policies Overview */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Policies</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <FileText className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">Total</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpiStats.total}</p>
          </div>
          <div className="p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-gray-700">Acknowledged</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpiStats.acknowledged}</p>
          </div>
          <div className="p-3 bg-yellow-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Clock className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-gray-700">Pending</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpiStats.pending}</p>
          </div>
        </div>

        {/* Policies List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Loading policies...</h3>
            <p className="text-xs text-gray-500 mt-1">
              Page {policiesCurrentPage} • Status: Active
            </p>
          </div>
        ) : policiesError || acknowledgmentsError ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-600 font-semibold">Error loading policies</p>
              {policiesError && <p className="text-xs text-red-500 mt-1">{policiesError.message}</p>}
              {acknowledgmentsError && <p className="text-xs text-red-500 mt-1">{acknowledgmentsError.message}</p>}
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
              >
                Reload Page
              </button>
            </div>
          </div>
        ) : policiesWithAcknowledgment.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No active policies found.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {policiesWithAcknowledgment.map((policy) => (
              <div key={policy.id || policy._id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900 capitalize truncate">{formatLabel(policy.title) || 'Untitled Policy'}</h4>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize flex-shrink-0">
                        {formatLabel(policy.category) || 'General'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2 truncate capitalize">{policy.description || 'No description available'}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500 flex-wrap">
                      <span className="truncate">Effective: {formatDate(policy.effectiveDate, { includeTime: false })}</span>
                      <span className="truncate">Updated: {formatDate(policy.updatedAt || policy.createdAt, { includeTime: false })}</span>
                      <span className="truncate">By: {formatLabel(policy.updatedBy || policy.createdBy) || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(policy.isAcknowledged ? 'completed' : 'pending')}`}>
                      {getStatusIcon(policy.isAcknowledged ? 'completed' : 'pending')}
                      <span className="ml-1 capitalize">{policy.isAcknowledged ? 'Acknowledged' : 'Pending'}</span>
                    </span>
                    {policy.isAcknowledged && policy.acknowledgedDate && (
                      <span className="text-xs text-gray-500">
                        {formatDate(policy.acknowledgedDate, { includeTime: false })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setSelectedPolicy(policy);
                        setShowPolicyModal(true);
                      }}
                      className="flex items-center space-x-1 px-3 py-1 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View</span>
                    </button>
                    <button
                      onClick={() => handleDownloadPolicy(policy)}
                      className="flex items-center space-x-1 px-3 py-1 text-gray-600 border border-blue-300 rounded-lg hover:bg-gray-50"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!policy.isAcknowledged && (
                      <button
                        onClick={() => handleAcknowledgePolicy(policy.id || policy._id)}
                        disabled={acknowledgeMutation.isPending}
                        className="flex items-center space-x-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>{acknowledgeMutation.isPending ? 'Acknowledging...' : 'Acknowledge'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && policiesTotal > 0 && (
          <div className="mt-6">
            <PaginationControls
              currentPage={policiesCurrentPage}
              totalPages={policiesTotalPages}
              totalItems={policiesTotal}
              limit={policiesItemsPerPage}
              onPageChange={(page) => {
                if (page < 1 || page > policiesTotalPages || page === policiesCurrentPage) return;
                setPoliciesCurrentPage(page);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              onLimitChange={(newLimit) => {
                // Optional: handle limit changes if needed
              }}
              hasNextPage={policiesCurrentPage < policiesTotalPages}
              hasPrevPage={policiesCurrentPage > 1}
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderPolicyModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900 capitalize break-words flex-1 min-w-0 pr-4">{formatLabel(selectedPolicy?.title) || 'Policy Details'}</h3>
          <button
            onClick={() => setShowPolicyModal(false)}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-4 text-sm text-gray-600 flex-wrap">
            <span className="break-words">Effective: {formatDate(selectedPolicy?.effectiveDate, { includeTime: false })}</span>
            <span className="break-words">Category: {formatLabel(selectedPolicy?.category) || 'N/A'}</span>
          </div>

          <div className="prose max-w-none">
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Description</h4>
              <p className="text-gray-700 whitespace-pre-wrap break-words capitalize">{selectedPolicy?.description || 'No description available'}</p>
            </div>
            {selectedPolicy?.content && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Content</h4>
                <div className="text-gray-700 whitespace-pre-wrap break-words">{selectedPolicy.content}</div>
              </div>
            )}
          </div>

          {selectedPolicy?.pdfUrl || selectedPolicy?.pdfFilePath ? (
            <div className="mt-4">
              <button
                onClick={() => handleDownloadPolicy(selectedPolicy)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                <span>Download PDF</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );


  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Policies</h1>
        </div>

        {/* Main Content */}
        {renderPolicies()}

        {/* Modals */}
        {showPolicyModal && renderPolicyModal()}
      </div>
    </div>
  );
};

export default PoliciesCompliance;
